import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Briefcase,
  Calendar,
  User,
  Clock,
  MapPin,
  Phone,
  MessageSquare,
  Paperclip,
  CheckCircle2,
  AlertCircle,
  Play,
  SkipForward,
  Shield,
  Users,
  ChevronDown,
  ChevronUp,
  Plus,
  Send,
  XCircle,
} from 'lucide-react';
import api from '../services/api';
import { Job, JobWorkflow, JobWorkflowStage, WorkflowStageStatus } from '../types';
import Layout from '../components/layout/Layout';

const statusConfig: Record<'RED' | 'YELLOW' | 'GREEN', { label: string; color: string; bgColor: string }> = {
  RED: { label: 'Action Required', color: 'text-status-red', bgColor: 'bg-status-red' },
  YELLOW: { label: 'In Progress', color: 'text-status-yellow', bgColor: 'bg-status-yellow' },
  GREEN: { label: 'Completed', color: 'text-status-green', bgColor: 'bg-status-green' },
};

const stageStatusConfig: Record<WorkflowStageStatus, { label: string; color: string; bgColor: string; icon: typeof CheckCircle2 }> = {
  PENDING: { label: 'Pending', color: 'text-dark-muted', bgColor: 'bg-dark-muted/20', icon: Clock },
  IN_PROGRESS: { label: 'In Progress', color: 'text-primary-500', bgColor: 'bg-primary-500/20', icon: Play },
  COMPLETED: { label: 'Completed', color: 'text-status-green', bgColor: 'bg-status-green/20', icon: CheckCircle2 },
  SKIPPED: { label: 'Skipped', color: 'text-dark-muted', bgColor: 'bg-dark-muted/10', icon: SkipForward },
  BLOCKED: { label: 'Blocked', color: 'text-status-red', bgColor: 'bg-status-red/20', icon: XCircle },
  AWAITING_APPROVAL: { label: 'Awaiting Approval', color: 'text-status-yellow', bgColor: 'bg-status-yellow/20', icon: Shield },
};

const priorityColors: Record<string, string> = {
  LOW: 'bg-dark-muted/20 text-dark-muted',
  MEDIUM: 'bg-primary-500/20 text-primary-500',
  HIGH: 'bg-status-yellow/20 text-status-yellow',
  URGENT: 'bg-status-red/20 text-status-red',
};

export default function JobDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<Job | null>(null);
  const [workflow, setWorkflow] = useState<JobWorkflow | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'workflow' | 'comments' | 'attachments' | 'stock'>('workflow');
  const [expandedStages, setExpandedStages] = useState<Set<number>>(new Set());
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [updatingStage, setUpdatingStage] = useState<number | null>(null);

  useEffect(() => {
    if (id) {
      fetchJobData();
    }
  }, [id]);

  const fetchJobData = async () => {
    try {
      setLoading(true);
      const [jobRes, workflowRes] = await Promise.all([
        api.get(`/jobs/${id}`),
        api.get(`/workflows/jobs/${id}`).catch(() => ({ data: { success: false } })),
      ]);

      if (jobRes.data.success) {
        setJob(jobRes.data.data);
      }
      if (workflowRes.data.success) {
        setWorkflow(workflowRes.data.data);
        // Auto-expand current stages
        const currentStages = workflowRes.data.data.stages.filter(
          (s: JobWorkflowStage) => s.status === 'IN_PROGRESS' || s.status === 'AWAITING_APPROVAL'
        );
        setExpandedStages(new Set(currentStages.map((s: JobWorkflowStage) => s.id)));
      }
    } catch (error) {
      console.error('Failed to fetch job data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleStageExpand = (stageId: number) => {
    const newExpanded = new Set(expandedStages);
    if (newExpanded.has(stageId)) {
      newExpanded.delete(stageId);
    } else {
      newExpanded.add(stageId);
    }
    setExpandedStages(newExpanded);
  };

  const handleStageStatusChange = async (stageId: number, newStatus: WorkflowStageStatus) => {
    try {
      setUpdatingStage(stageId);
      await api.put(`/workflows/jobs/stages/${stageId}`, { status: newStatus });
      await fetchJobData();
    } catch (error) {
      console.error('Failed to update stage:', error);
    } finally {
      setUpdatingStage(null);
    }
  };

  const handleApproveStage = async (stageId: number) => {
    try {
      setUpdatingStage(stageId);
      await api.post(`/workflows/jobs/stages/${stageId}/approve`);
      await fetchJobData();
    } catch (error) {
      console.error('Failed to approve stage:', error);
    } finally {
      setUpdatingStage(null);
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      setSubmittingComment(true);
      await api.post(`/jobs/${id}/comments`, { comment: newComment });
      setNewComment('');
      await fetchJobData();
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (minutes?: number) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getProgressPercent = () => {
    if (!workflow || workflow.stages.length === 0) return 0;
    const completed = workflow.stages.filter(s => s.status === 'COMPLETED' || s.status === 'SKIPPED').length;
    return Math.round((completed / workflow.stages.length) * 100);
  };

  // Group stages by parallel_group for visual representation
  const getGroupedStages = () => {
    if (!workflow) return [];

    const groups: { group: number | undefined; stages: JobWorkflowStage[] }[] = [];
    let currentGroup: number | undefined = undefined;
    let currentStages: JobWorkflowStage[] = [];

    workflow.stages.forEach((stage) => {
      if (stage.can_run_parallel && stage.parallel_group !== undefined) {
        if (stage.parallel_group === currentGroup) {
          currentStages.push(stage);
        } else {
          if (currentStages.length > 0) {
            groups.push({ group: currentGroup, stages: currentStages });
          }
          currentGroup = stage.parallel_group;
          currentStages = [stage];
        }
      } else {
        if (currentStages.length > 0) {
          groups.push({ group: currentGroup, stages: currentStages });
          currentStages = [];
          currentGroup = undefined;
        }
        groups.push({ group: undefined, stages: [stage] });
      }
    });

    if (currentStages.length > 0) {
      groups.push({ group: currentGroup, stages: currentStages });
    }

    return groups;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      </Layout>
    );
  }

  if (!job) {
    return (
      <Layout>
        <div className="p-6">
          <div className="text-center py-12">
            <Briefcase size={48} className="mx-auto mb-4 opacity-50 text-dark-muted" />
            <p className="text-dark-muted">Job not found</p>
            <button onClick={() => navigate('/jobs')} className="btn btn-primary mt-4">
              Back to Jobs
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const groupedStages = getGroupedStages();
  const progressPercent = getProgressPercent();

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate('/jobs')} className="p-2 hover:bg-dark-elevated rounded-lg transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <span className="text-sm font-mono text-dark-muted">{job.ticket_number}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${priorityColors[job.priority]}`}>
                {job.priority}
              </span>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${statusConfig[job.status].bgColor}`} />
                <span className={`text-sm ${statusConfig[job.status].color}`}>
                  {statusConfig[job.status].label}
                </span>
              </div>
            </div>
            <h1 className="text-2xl font-semibold mt-1">{job.title}</h1>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Progress Bar */}
            {workflow && (
              <div className="card">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium">Workflow Progress</h3>
                  <span className="text-sm text-dark-muted">
                    {workflow.stages.filter(s => s.status === 'COMPLETED').length} / {workflow.stages.length} stages
                  </span>
                </div>
                <div className="h-3 bg-dark-elevated rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-primary-500 to-status-green transition-all duration-500"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <div className="flex justify-between mt-2 text-xs text-dark-muted">
                  <span>{progressPercent}% Complete</span>
                  {workflow.completed_at && <span>Completed {formatDate(workflow.completed_at)}</span>}
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="flex gap-1 border-b border-dark-border">
              {[
                { key: 'workflow', label: 'Workflow', icon: Play },
                { key: 'comments', label: 'Comments', icon: MessageSquare, count: job.comments?.length },
                { key: 'attachments', label: 'Attachments', icon: Paperclip, count: job.attachments?.length },
                { key: 'stock', label: 'Stock', icon: Briefcase },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as typeof activeTab)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-primary-500 text-primary-500'
                      : 'border-transparent text-dark-muted hover:text-white'
                  }`}
                >
                  <tab.icon size={16} />
                  {tab.label}
                  {tab.count !== undefined && tab.count > 0 && (
                    <span className="text-xs bg-dark-elevated px-1.5 py-0.5 rounded-full">{tab.count}</span>
                  )}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            {activeTab === 'workflow' && (
              <div className="space-y-4">
                {!workflow ? (
                  <div className="card text-center py-8">
                    <Play size={32} className="mx-auto mb-2 text-dark-muted" />
                    <p className="text-dark-muted">No workflow assigned to this job</p>
                    <button className="btn btn-primary mt-4">
                      <Plus size={16} className="mr-2" />
                      Create Workflow
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-dark-border" />

                    {groupedStages.map((group, groupIndex) => (
                      <div key={groupIndex} className="relative">
                        {group.stages.length > 1 ? (
                          // Parallel stages
                          <div className="ml-14 mb-4">
                            <div className="text-xs text-dark-muted mb-2 flex items-center gap-2">
                              <Users size={12} />
                              Parallel Tasks (Group {group.group})
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {group.stages.map((stage) => (
                                <StageCard
                                  key={stage.id}
                                  stage={stage}
                                  isExpanded={expandedStages.has(stage.id)}
                                  onToggle={() => toggleStageExpand(stage.id)}
                                  onStatusChange={handleStageStatusChange}
                                  onApprove={handleApproveStage}
                                  isUpdating={updatingStage === stage.id}
                                  compact
                                />
                              ))}
                            </div>
                          </div>
                        ) : (
                          // Sequential stage
                          <div className="flex gap-4 mb-4">
                            {/* Timeline dot */}
                            <div className="relative z-10 flex items-center justify-center w-12 h-12">
                              <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                  stageStatusConfig[group.stages[0].status].bgColor
                                }`}
                              >
                                {(() => {
                                  const Icon = stageStatusConfig[group.stages[0].status].icon;
                                  return <Icon size={20} className={stageStatusConfig[group.stages[0].status].color} />;
                                })()}
                              </div>
                            </div>

                            <div className="flex-1">
                              <StageCard
                                stage={group.stages[0]}
                                isExpanded={expandedStages.has(group.stages[0].id)}
                                onToggle={() => toggleStageExpand(group.stages[0].id)}
                                onStatusChange={handleStageStatusChange}
                                onApprove={handleApproveStage}
                                isUpdating={updatingStage === group.stages[0].id}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'comments' && (
              <div className="space-y-4">
                {/* Add Comment */}
                <form onSubmit={handleAddComment} className="card">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    className="input w-full resize-none"
                    rows={3}
                  />
                  <div className="flex justify-end mt-3">
                    <button
                      type="submit"
                      disabled={!newComment.trim() || submittingComment}
                      className="btn btn-primary flex items-center gap-2"
                    >
                      <Send size={16} />
                      {submittingComment ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                </form>

                {/* Comments List */}
                {job.comments && job.comments.length > 0 ? (
                  <div className="space-y-3">
                    {job.comments.map((comment) => (
                      <div key={comment.id} className="card">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-500 text-sm">
                            {comment.user_name?.charAt(0) || 'U'}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-sm">{comment.user_name || 'Unknown'}</span>
                              <span className="text-xs text-dark-muted">
                                {new Date(comment.created_at).toLocaleString('en-ZA')}
                              </span>
                              {comment.is_internal && (
                                <span className="text-xs bg-status-yellow/20 text-status-yellow px-1.5 py-0.5 rounded">
                                  Internal
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-dark-muted whitespace-pre-wrap">{comment.comment}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="card text-center py-8">
                    <MessageSquare size={32} className="mx-auto mb-2 text-dark-muted" />
                    <p className="text-dark-muted">No comments yet</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'attachments' && (
              <div className="card text-center py-8">
                <Paperclip size={32} className="mx-auto mb-2 text-dark-muted" />
                <p className="text-dark-muted">No attachments yet</p>
                <button className="btn btn-secondary mt-4">
                  <Plus size={16} className="mr-2" />
                  Upload File
                </button>
              </div>
            )}

            {activeTab === 'stock' && (
              <div className="card text-center py-8">
                <Briefcase size={32} className="mx-auto mb-2 text-dark-muted" />
                <p className="text-dark-muted">No stock allocated</p>
                <button className="btn btn-secondary mt-4">
                  <Plus size={16} className="mr-2" />
                  Allocate Stock
                </button>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Job Details */}
            <div className="card">
              <h3 className="font-medium mb-4">Job Details</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <User size={16} className="text-dark-muted mt-0.5" />
                  <div>
                    <p className="text-dark-muted">Customer</p>
                    <p className="font-medium">{job.customer_name || '-'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar size={16} className="text-dark-muted mt-0.5" />
                  <div>
                    <p className="text-dark-muted">Due Date</p>
                    <p className="font-medium">{formatDate(job.due_date)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <User size={16} className="text-dark-muted mt-0.5" />
                  <div>
                    <p className="text-dark-muted">Assigned To</p>
                    <p className="font-medium">{job.assigned_to_name || 'Unassigned'}</p>
                  </div>
                </div>
                {job.site_address && (
                  <div className="flex items-start gap-3">
                    <MapPin size={16} className="text-dark-muted mt-0.5" />
                    <div>
                      <p className="text-dark-muted">Site Address</p>
                      <p className="font-medium">{job.site_address}</p>
                    </div>
                  </div>
                )}
                {job.site_contact_phone && (
                  <div className="flex items-start gap-3">
                    <Phone size={16} className="text-dark-muted mt-0.5" />
                    <div>
                      <p className="text-dark-muted">Site Contact</p>
                      <p className="font-medium">
                        {job.site_contact_name && `${job.site_contact_name} • `}
                        {job.site_contact_phone}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            {job.description && (
              <div className="card">
                <h3 className="font-medium mb-3">Description</h3>
                <p className="text-sm text-dark-muted whitespace-pre-wrap">{job.description}</p>
              </div>
            )}

            {/* Time Tracking */}
            {workflow && (
              <div className="card">
                <h3 className="font-medium mb-3">Time Tracking</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-dark-muted">Total Time</span>
                    <span className="font-medium">
                      {formatTime(workflow.stages.reduce((sum, s) => sum + (s.time_spent_minutes || 0), 0))}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-dark-muted">Estimated</span>
                    <span className="font-medium">{job.estimated_hours ? `${job.estimated_hours}h` : '-'}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="card">
              <h3 className="font-medium mb-3">Quick Actions</h3>
              <div className="space-y-2">
                <button className="btn btn-secondary w-full justify-start">
                  <Plus size={16} className="mr-2" />
                  Add Stage
                </button>
                <button className="btn btn-secondary w-full justify-start">
                  <MessageSquare size={16} className="mr-2" />
                  Add Comment
                </button>
                <button className="btn btn-secondary w-full justify-start">
                  <Paperclip size={16} className="mr-2" />
                  Upload File
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

// Stage Card Component
function StageCard({
  stage,
  isExpanded,
  onToggle,
  onStatusChange,
  onApprove,
  isUpdating,
  compact,
}: {
  stage: JobWorkflowStage;
  isExpanded: boolean;
  onToggle: () => void;
  onStatusChange: (stageId: number, status: WorkflowStageStatus) => void;
  onApprove: (stageId: number) => void;
  isUpdating: boolean;
  compact?: boolean;
}) {
  const config = stageStatusConfig[stage.status];

  const formatTime = (minutes?: number) => {
    if (!minutes) return null;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  return (
    <div className={`card ${compact ? 'p-3' : ''} ${isUpdating ? 'opacity-50' : ''}`}>
      <div className="flex items-start justify-between cursor-pointer" onClick={onToggle}>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium">{stage.name}</span>
            {!stage.is_required && (
              <span className="text-xs bg-dark-muted/20 text-dark-muted px-1.5 py-0.5 rounded">Optional</span>
            )}
            {stage.requires_approval && (
              <span title="Requires Approval">
                <Shield size={12} className="text-status-yellow" />
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-dark-muted">
            <span className={`flex items-center gap-1 ${config.color}`}>
              <config.icon size={12} />
              {config.label}
            </span>
            {stage.department_name && (
              <span className="flex items-center gap-1">
                <Users size={12} />
                {stage.department_name}
              </span>
            )}
            {stage.assigned_to_name && (
              <span className="flex items-center gap-1">
                <User size={12} />
                {stage.assigned_to_name}
              </span>
            )}
            {stage.time_spent_minutes && (
              <span className="flex items-center gap-1">
                <Clock size={12} />
                {formatTime(stage.time_spent_minutes)}
              </span>
            )}
          </div>
        </div>
        <button className="p-1 hover:bg-dark-elevated rounded transition-colors">
          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-dark-border">
          {stage.description && (
            <p className="text-sm text-dark-muted mb-4">{stage.description}</p>
          )}

          {stage.notes && (
            <div className="bg-dark-elevated rounded-lg p-3 mb-4">
              <p className="text-xs text-dark-muted mb-1">Notes</p>
              <p className="text-sm">{stage.notes}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            {stage.status === 'PENDING' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onStatusChange(stage.id, 'IN_PROGRESS');
                }}
                disabled={isUpdating}
                className="btn btn-primary btn-sm"
              >
                <Play size={14} className="mr-1" />
                Start
              </button>
            )}

            {stage.status === 'IN_PROGRESS' && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onStatusChange(stage.id, 'COMPLETED');
                  }}
                  disabled={isUpdating}
                  className="btn btn-primary btn-sm"
                >
                  <CheckCircle2 size={14} className="mr-1" />
                  Complete
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onStatusChange(stage.id, 'BLOCKED');
                  }}
                  disabled={isUpdating}
                  className="btn btn-secondary btn-sm"
                >
                  <AlertCircle size={14} className="mr-1" />
                  Block
                </button>
              </>
            )}

            {stage.status === 'AWAITING_APPROVAL' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onApprove(stage.id);
                }}
                disabled={isUpdating}
                className="btn btn-primary btn-sm"
              >
                <Shield size={14} className="mr-1" />
                Approve
              </button>
            )}

            {stage.status === 'BLOCKED' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onStatusChange(stage.id, 'IN_PROGRESS');
                }}
                disabled={isUpdating}
                className="btn btn-secondary btn-sm"
              >
                <Play size={14} className="mr-1" />
                Unblock
              </button>
            )}

            {(stage.status === 'PENDING' || stage.status === 'IN_PROGRESS') && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onStatusChange(stage.id, 'SKIPPED');
                }}
                disabled={isUpdating}
                className="btn btn-ghost btn-sm"
              >
                <SkipForward size={14} className="mr-1" />
                Skip
              </button>
            )}
          </div>

          {/* Approval Info */}
          {stage.approved_by_name && stage.approved_at && (
            <div className="mt-3 text-xs text-dark-muted">
              Approved by {stage.approved_by_name} on {new Date(stage.approved_at).toLocaleString('en-ZA')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
